import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useAppStateContext } from '@/shared';
import { PageLoading } from '@/shared/components/custom';
import { usePagePlayer } from '../hooks';
import { ITEM_RENDERERS } from '../components/items';
import { WallStage } from '../components/WallStage';
import { itemType } from '../lib/items';
import LogoMark from '@/assets/logo-mark.svg';
import LogoWhite from '@/assets/logo-horizontal-white.svg';
import 'animate.css';

const getPlayerScreen = (state, screenInfo, getIdleImage) => {
  const { transitionEntry, transitionExit } = state.activeItem.playlist;
  const item = state.currentItem;
  const Renderer = ITEM_RENDERERS[itemType(item)];
  return (
    <CSSTransition
      classNames={{
        enter: 'animate__animated',
        enterActive: transitionEntry ? transitionEntry : 'animate__fadeIn',
        exit: 'animate__animated',
        exitActive: transitionExit ? transitionExit : 'animate__fadeOut',
      }}
      timeout={1000}
      key={`${item.contentId ?? item.file?.fileName}-${state.currentIndex}`}
    >
      {/* Wrapper so the transition classes apply to every item type alike. */}
      <div className="absolute inset-0">
        <Renderer
          item={item}
          single={state.items.length === 1}
          onEnded={state.advance}
          clock={state.clock}
          startedAtMs={state.itemStartedAtMs}
          idleImage={getIdleImage(screenInfo?.orientation)}
        />
      </div>
    </CSSTransition>
  );
};

export const PagePlayer = () => {
  const { appState } = useAppStateContext();
  const { state, getIdleImage } = usePagePlayer();

  const screenInfo = appState.screenInfo;

  // Nothing playable (e.g. only web pages while offline) shows the idle image.
  const isPlayerContentAvailable =
    state.activeItem.scheduleId > 0 && !!state.currentItem;

  // Plan-driven flags carried on the schedule bundle.
  const isPaused = !!state.bundle?.paused;
  const showBranding = !!state.bundle?.showBranding;

  return state.isBusy ? (
    <PageLoading text="Loading contents..." />
  ) : (
    <div className="w-screen h-screen bg-black overflow-hidden relative">
      {isPaused ? (
        <PausedSlate />
      ) : isPlayerContentAvailable && state.wall ? (
        <WallStage wall={state.wall} now={state.now}>
          <TransitionGroup>
            {getPlayerScreen(state, screenInfo, getIdleImage)}
          </TransitionGroup>
        </WallStage>
      ) : isPlayerContentAvailable ? (
        <TransitionGroup>
          {getPlayerScreen(state, screenInfo, getIdleImage)}
        </TransitionGroup>
      ) : (
        <img
          alt="idle"
          className="h-screen w-screen object-contain"
          src={getIdleImage(screenInfo.orientation)}
        />
      )}
      {showBranding && !isPaused && <BrandingBadge />}
      {state.identify && <IdentifyOverlay label={state.identify} />}
    </div>
  );
};

// Free plan: small, unobtrusive corner badge over playback.
const BrandingBadge = () => (
  <div className="absolute bottom-4 right-4 z-50 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 text-xs font-medium text-white/90 backdrop-blur-sm pointer-events-none select-none">
    <img src={LogoMark} alt="" className="h-4 w-4" />
    <span>Powered by PixelPlay</span>
  </div>
);

// Video wall "Identify": which wall and cell this screen is, to check the cabling.
const IdentifyOverlay = ({ label }) => (
  <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/70 pointer-events-none select-none">
    <p className="rounded-2xl border-4 border-white px-12 py-8 text-center text-7xl font-bold text-white">
      {label}
    </p>
  </div>
);

// Screen is beyond the plan's screen limit (e.g. after dropping to Free).
const PausedSlate = () => (
  <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 bg-black px-8 text-center text-white">
    <img src={LogoWhite} alt="PixelPlay" className="h-10" />
    <div className="space-y-2">
      <p className="text-2xl font-semibold">This screen is paused</p>
      <p className="text-base text-white/70">
        Your plan's screen limit has been reached. Upgrade your plan to resume
        playback.
      </p>
    </div>
  </div>
);
